import React, { useEffect, useMemo, useState } from "react";
import { Banknote, Gift, Loader2, X } from "lucide-react";
import { ApiService } from "../../../services/api";
import UserSelector from "../../../components/selectors/UserSelector";
import { EMPTY_BILLING_INFO, BillingInfo } from "../../../types/payment";

interface GrantPackageModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  // Üyeler ekranından açıldığında kişi hazır gelir.
  presetUser?: { id: string; firstName?: string; lastName?: string } | null;
}

const INPUT_CLASS = "w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100";
const LABEL_CLASS = "mb-1 block text-xs font-semibold text-slate-600";

function toLocalInputValue(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export default function GrantPackageModal({ open, onClose, onSuccess, presetUser = null }: GrantPackageModalProps) {
  const [userId, setUserId] = useState<string>("");
  const [packages, setPackages] = useState<any[]>([]);
  const [packageId, setPackageId] = useState<string>("");
  const [customEndsAt, setCustomEndsAt] = useState<string>("");
  const [method, setMethod] = useState<"cash" | "free">("cash");
  const [amount, setAmount] = useState<string>("");
  const [note, setNote] = useState<string>("");
  const [billingOpen, setBillingOpen] = useState(false);
  const [billing, setBilling] = useState<BillingInfo>({ ...EMPTY_BILLING_INFO });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    setUserId(presetUser?.id || "");
    setMethod("cash");
    setNote("");
    setBilling({ ...EMPTY_BILLING_INFO });
    setBillingOpen(false);
    const nextWeek = new Date();
    nextWeek.setDate(nextWeek.getDate() + 7);
    setCustomEndsAt(toLocalInputValue(nextWeek));

    setLoading(true);
    ApiService.getGrantablePaymentPackages()
      .then((data) => {
        const list = data?.packages || [];
        setPackages(list);
        const firstActive = list.find((item: any) => item.active) || list[0];
        setPackageId(firstActive?.id || "");
        setAmount(firstActive?.price ? String(firstActive.price) : "");
      })
      .catch((err) => setError(err?.message || "Paket listesi alınamadı."))
      .finally(() => setLoading(false));
  }, [open, presetUser?.id]);

  const selectedPackage = useMemo(
    () => packages.find((item) => item.id === packageId),
    [packages, packageId]
  );

  // Paket değişince nakit tutarını paketin fiyatıyla öner (admin değiştirebilir).
  useEffect(() => {
    if (method === "free") return;
    if (packageId === "custom") return;
    if (selectedPackage?.price) setAmount(String(selectedPackage.price));
  }, [packageId, selectedPackage, method]);

  if (!open) return null;

  const handleSubmit = async () => {
    setError("");
    if (!userId) {
      setError("Lütfen kullanıcı seçin.");
      return;
    }
    if (!packageId) {
      setError("Lütfen paket seçin.");
      return;
    }
    if (packageId === "custom" && !customEndsAt) {
      setError("Özel bitiş tarihi girin.");
      return;
    }
    if (method === "cash" && !(Number(amount) > 0)) {
      setError("Nakit ödeme için tahsil edilen tutarı girin.");
      return;
    }

    setSaving(true);
    try {
      await ApiService.grantPaymentPackage({
        userId,
        packageId,
        customEndsAt: packageId === "custom" ? new Date(customEndsAt).toISOString() : undefined,
        method,
        amount: method === "cash" ? Number(amount) : 0,
        note: note.trim() || undefined,
        billing: billingOpen ? billing : undefined,
      });
      onSuccess?.();
      onClose();
    } catch (err: any) {
      setError(err?.message || "Paket tanımlanamadı.");
    } finally {
      setSaving(false);
    }
  };

  const setBillingField = (field: keyof BillingInfo) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = event.target.value;
    setBilling((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/40 p-4">
      <div className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Paket Tanımla</h2>
            <p className="text-xs text-slate-500">Nakit tahsilat veya ücretsiz erişim için kişiye süreli paket verin.</p>
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto px-5 py-5">
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
            </div>
          ) : (
            <>
              <div>
                <label className={LABEL_CLASS}>Kullanıcı</label>
                {presetUser?.id ? (
                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-medium text-slate-800">
                    {`${presetUser.firstName || ""} ${presetUser.lastName || ""}`.trim() || presetUser.id}
                  </div>
                ) : (
                  <UserSelector value={userId} onChange={(value) => setUserId(value)} placeholder="Kişi arayın" />
                )}
              </div>

              <div>
                <label className={LABEL_CLASS}>Ödeme Yöntemi</label>
                <div className="grid grid-cols-2 gap-3">
                  {([
                    { id: "cash", label: "Nakit Tahsilat", icon: Banknote, hint: "Elden/havale alınan ödeme" },
                    { id: "free", label: "Ücretsiz", icon: Gift, hint: "Hediye / promosyon erişimi" },
                  ] as const).map((option) => {
                    const Icon = option.icon;
                    const selected = method === option.id;
                    return (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => setMethod(option.id)}
                        className={`flex items-start gap-3 rounded-xl border p-3 text-left transition ${
                          selected ? "border-blue-600 bg-blue-50" : "border-slate-200 bg-white hover:border-slate-300"
                        }`}
                      >
                        <Icon className={`mt-0.5 h-4 w-4 ${selected ? "text-blue-600" : "text-slate-400"}`} />
                        <div>
                          <div className={`text-sm font-semibold ${selected ? "text-blue-700" : "text-slate-700"}`}>{option.label}</div>
                          <div className="text-[11px] text-slate-500">{option.hint}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className={LABEL_CLASS}>Paket</label>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  {packages.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setPackageId(item.id)}
                      className={`rounded-xl border px-3 py-2.5 text-left transition ${
                        packageId === item.id ? "border-blue-600 bg-blue-50" : "border-slate-200 bg-white hover:border-slate-300"
                      }`}
                    >
                      <div className={`text-xs font-semibold ${packageId === item.id ? "text-blue-700" : "text-slate-700"}`}>{item.label}</div>
                      <div className="text-[11px] text-slate-500">
                        {item.price > 0
                          ? Number(item.price).toLocaleString("tr-TR", { style: "currency", currency: "TRY" })
                          : "Fiyat tanımsız"}
                      </div>
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => setPackageId("custom")}
                    className={`rounded-xl border px-3 py-2.5 text-left transition ${
                      packageId === "custom" ? "border-blue-600 bg-blue-50" : "border-slate-200 bg-white hover:border-slate-300"
                    }`}
                  >
                    <div className={`text-xs font-semibold ${packageId === "custom" ? "text-blue-700" : "text-slate-700"}`}>Özel Tarih</div>
                    <div className="text-[11px] text-slate-500">Bitişi kendin seç</div>
                  </button>
                </div>
                {packageId !== "custom" && (
                  <p className="mt-2 text-[11px] text-slate-500">
                    {selectedPackage?.durationType === "fixed" && selectedPackage.endsOn
                      ? `Bu paket herkes için ${new Date(`${selectedPackage.endsOn}T12:00:00`).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" })} günü sonunda biter.`
                      : "Kişinin aynı türden geçerli paketi varsa süre onun bitişinin ÜZERİNE eklenir; farklı türden paketleri ayrı ayrı sürer. Günlük paket ertesi gün saat 07:30'da biter."}
                  </p>
                )}
              </div>

              {packageId === "custom" && (
                <div>
                  <label className={LABEL_CLASS}>Erişim Bitiş Tarihi</label>
                  <input
                    type="datetime-local"
                    value={customEndsAt}
                    onChange={(e) => setCustomEndsAt(e.target.value)}
                    className={INPUT_CLASS}
                  />
                </div>
              )}

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className={LABEL_CLASS}>Tahsil Edilen Tutar (₺)</label>
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    value={method === "free" ? "0" : amount}
                    disabled={method === "free"}
                    onChange={(e) => setAmount(e.target.value)}
                    className={`${INPUT_CLASS} ${method === "free" ? "bg-slate-100 text-slate-400" : ""}`}
                  />
                </div>
                <div>
                  <label className={LABEL_CLASS}>Açıklama</label>
                  <input
                    type="text"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Örn. elden tahsil edildi, makbuz no 124"
                    className={INPUT_CLASS}
                  />
                </div>
              </div>

              <div className="rounded-xl border border-slate-200">
                <button
                  type="button"
                  onClick={() => setBillingOpen((prev) => !prev)}
                  className="flex w-full items-center justify-between px-4 py-3 text-sm font-semibold text-slate-700"
                >
                  <span>Fatura Bilgileri <span className="font-normal text-slate-400">(opsiyonel)</span></span>
                  <span className="text-xs text-blue-600">{billingOpen ? "Gizle" : "Ekle"}</span>
                </button>
                {billingOpen && (
                  <div className="grid gap-3 border-t border-slate-200 p-4 sm:grid-cols-2">
                    <div>
                      <label className={LABEL_CLASS}>Fatura Tipi</label>
                      <select
                        value={billing.type}
                        onChange={(e) => setBilling((prev) => ({ ...prev, type: e.target.value as BillingInfo["type"] }))}
                        className={INPUT_CLASS}
                      >
                        <option value="individual">Bireysel</option>
                        <option value="corporate">Kurumsal</option>
                      </select>
                    </div>
                    {billing.type === "corporate" ? (
                      <>
                        <div>
                          <label className={LABEL_CLASS}>Firma Unvanı</label>
                          <input type="text" value={billing.companyName} onChange={setBillingField("companyName")} className={INPUT_CLASS} />
                        </div>
                        <div>
                          <label className={LABEL_CLASS}>Vergi Dairesi</label>
                          <input type="text" value={billing.taxOffice} onChange={setBillingField("taxOffice")} className={INPUT_CLASS} />
                        </div>
                        <div>
                          <label className={LABEL_CLASS}>Vergi No</label>
                          <input type="text" value={billing.taxNumber} onChange={setBillingField("taxNumber")} className={INPUT_CLASS} />
                        </div>
                      </>
                    ) : (
                      <div>
                        <label className={LABEL_CLASS}>TC Kimlik No</label>
                        <input type="text" value={billing.identityNumber} onChange={setBillingField("identityNumber")} className={INPUT_CLASS} />
                      </div>
                    )}
                    <div>
                      <label className={LABEL_CLASS}>Ad</label>
                      <input type="text" value={billing.firstName} onChange={setBillingField("firstName")} className={INPUT_CLASS} />
                    </div>
                    <div>
                      <label className={LABEL_CLASS}>Soyad</label>
                      <input type="text" value={billing.lastName} onChange={setBillingField("lastName")} className={INPUT_CLASS} />
                    </div>
                    <div>
                      <label className={LABEL_CLASS}>Telefon</label>
                      <input type="text" value={billing.phone} onChange={setBillingField("phone")} className={INPUT_CLASS} />
                    </div>
                    <div>
                      <label className={LABEL_CLASS}>E-posta</label>
                      <input type="text" value={billing.email} onChange={setBillingField("email")} className={INPUT_CLASS} />
                    </div>
                    <div className="sm:col-span-2">
                      <label className={LABEL_CLASS}>Fatura Adresi</label>
                      <textarea rows={2} value={billing.address} onChange={setBillingField("address")} className={INPUT_CLASS} />
                    </div>
                    <div>
                      <label className={LABEL_CLASS}>İlçe</label>
                      <input type="text" value={billing.district} onChange={setBillingField("district")} className={INPUT_CLASS} />
                    </div>
                    <div>
                      <label className={LABEL_CLASS}>İl</label>
                      <input type="text" value={billing.city} onChange={setBillingField("city")} className={INPUT_CLASS} />
                    </div>
                  </div>
                )}
              </div>

              {error && <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{error}</div>}
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-5 py-4">
          <button onClick={onClose} className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100">
            Vazgeç
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving || loading}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Paketi Tanımla
          </button>
        </div>
      </div>
    </div>
  );
}
